import { useState, useRef, useEffect } from "react";
import { BlogCommentEntity } from "../../../types";

interface UseCommentEditStateProps {
  comment: BlogCommentEntity;
  startInEditMode?: boolean;
  onEdit?: (comment: BlogCommentEntity, body: string) => void;
}

export function useCommentEditState({
  comment,
  startInEditMode = false,
  onEdit,
}: UseCommentEditStateProps) {
  const editableRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(startInEditMode);

  useEffect(() => {
    if (isEditing) {
      const el = editableRef.current;
      // Move cursor to end when editing
      if (el) {
        el.focus();

        const range = document.createRange();
        range.selectNodeContents(el);
        range.collapse(false);

        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [isEditing]);

  const startEditing = () => setIsEditing(true);

  const cancelEditing = () => {
    if (editableRef.current) {
      // For editing, restore plain text content without HTML styling
      const plainTextContent = (comment.parsed_body ?? "").replace(
        /<[^>]*>/g,
        "",
      );
      editableRef.current.innerHTML = plainTextContent;
    }
    setIsEditing(false);
  };

  const submitEdit = () => {
    if (editableRef.current && onEdit) {
      const content = editableRef.current.innerText.trim();
      onEdit(comment, content);
      setIsEditing(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLDivElement>) => {
    const content = e.currentTarget.innerText.trim();
    if (onEdit) {
      onEdit(comment, content);
      setIsEditing(false);
    }
  };

  return {
    editableRef,
    isEditing,
    startEditing,
    cancelEditing,
    submitEdit,
    handleSubmit,
  };
}
